<i18n lang="yaml">
en:
  month: 'month'
  from: 'from'
ru:
  month: 'мес.'
  from: 'от'
</i18n>

<template>
  <s-section is-sub-section>
    <table-main
      :titles="titles"
      is-scrolled-table
      class="dedicated-servers-table"
    >
      <table-title-column :titles="titles">
        <template
          v-for="title in titlesSlots"
          #[`#${title.dataIndex}`]="{title}"
        >
          <div :key="title.name">
            {{ title.name }}
            <a-tooltip placement="top">
              <template slot="title">
                <div v-html="title.tooltip"></div>
              </template>
              <i class="tooltip-icon">?</i>
            </a-tooltip>
          </div>
        </template>
      </table-title-column>

      <table-column
        v-for="(item, i) in [readyDesktop, readyServer, randomConfig]"
        :key="i"
        :titles="titles"
        :column="item"
      >
        <template
          v-for="title in titlesSlots"
          #[`#${title.dataIndex}`]="{title}"
        >
          <div :key="title.name">
            {{ title.name }}
            <a-tooltip placement="top">
              <template slot="title">
                <div v-html="title.tooltip"></div>
              </template>
              <i class="tooltip-icon">?</i>
            </a-tooltip>
          </div>
        </template>

        <template #header="{column}">
          <div
            v-if="column.header.title"
            :class="[
              'column-header',
              'column-header--ready',
              column.header.dataHidden
                ? 'column-header--hidden'
                : '']"
          >
            <h3 class="column-header__global-title">
              {{ column.header.globalTitle }}
            </h3>
            <span class="column-header__description">
              {{ column.header.subtitle }}
            </span>

            <h4 class="column-header__title">
              {{ column.header.title }}
              <a-tooltip placement="top">
                <template slot="title">
                  <div v-html="column.header.tooltip"></div>
                </template>
                <i class="tooltip-icon">?</i>
              </a-tooltip>
            </h4>
          </div>

          <div
            v-else
            class="column-header column-header--random"
          >
            <h3>{{ column.header.globalTitle }}</h3>
            <span class="column-header__description">{{ column.header.subtitle }}</span>
          </div>
        </template>

        <template #trafficVolume="{column}">
          <p
            v-for="volume in column.trafficVolume"
            :key="volume"
          >
            {{ volume }}
          </p>
        </template>

        <template #price="{column}">
          <p class="h4 dedicated-servers-table__price">
            {{ $t('from') }} {{ $priceFormatter(prices[column.price.target]) }}/{{ $t('month') }}
          </p>
          <p class="dedicated-servers-table__price-description">
            {{ column.price.subtitle }}
          </p>
          <a
            v-scroll-to="`#${column.price.scrollTo}`"
            class="btn btn--dark btn--responsive"
            href="#"
            :data-gtmid="column.price.dataGtmid"
            @click="setActiveTab(column.price.eventData)"
          >
            {{ column.price.buttonText }}
          </a>
        </template>

        <template
          v-for="yesSlot in item.yesIcons"
          #[yesSlot]
        >
          <svg-yes-icon :key="yesSlot"></svg-yes-icon>
        </template>

        <template
          v-for="noSlot in item.noIcons"
          #[noSlot]
        >
          <svg-no-icon :key="noSlot"></svg-no-icon>
        </template>
      </table-column>
    </table-main>
  </s-section>
</template>

<script>
import TableMain from '~/components/common/table/TableMain'
import TableTitleColumn from '~/components/common/table/TableTitleColumn'
import TableColumn from '~/components/common/table/TableColumn'

import SvgNoIcon from '~/assets/icons/tables/no.svg'
import SvgYesIcon from '~/assets/icons/tables/yes.svg'

import RestClient from '~/rest-client'
import addAdditionalData from '~/mixins/servers'

import {TariffLine} from '~/rest-client/servers/all/enum'
import {mapActions, mapGetters} from 'vuex'

const titles = {
  en: [
    {
      dataIndex: 'header',
      columnSlot: true
    },
    {
      name: 'Readiness',
      dataIndex: 'readyToWork'
    },
    {
      name: '1 shared public IPv4 address',
      dataIndex: 'publicIpV4',
      columnSlot: true
    },
    {
      name: 'OS fully automatic installation and Rescue mode',
      tooltip: `
      <p>For dedicated servers, Selectel offers
        <a
          href="https://kb.selectel.com/docs/servers-and-infrastructure/dedicated-servers/getting-started/automated_os_installation/"
          target="_blank"
          rel="noopener noreferrer"
        >
          OS FAI</a>.
      </p>
      <p>When your operating system fails and there is&nbsp;no&nbsp;access to&nbsp;the console, Rescue mode comes in&nbsp;handy. It&nbsp;will start an&nbsp;OS unattended and independent of&nbsp;the host system, running it&nbsp;in&nbsp;RAM only.</p>
      <p>This OS&nbsp;is&nbsp;bundled with diagnostic tools that can be&nbsp;used to&nbsp;regain access to&nbsp;the server and the data stored on&nbsp;it.</p>
    `,
      dataIndex: 'autoInstall',
      titleSlot: true,
      columnSlot: true
    },
    {
      name: 'KVM Console',
      tooltip: `
      <p>
        <a
          href="https://kb.selectel.com/docs/servers-and-infrastructure/dedicated-servers/getting-started/kvm_console/"
          target="_blank"
          rel="noopener noreferrer"
        >
          KVM
        </a>
        (Keyboard, Video, Mouse) is&nbsp;a&nbsp;technology that simulates a&nbsp;local connection to&nbsp;a&nbsp;server and streams video from the server to&nbsp;the browser. It&nbsp;allows remotely controlling equipment and using basic IPMI&nbsp;features.      </p>
    `,
      dataIndex: 'kvmConsole',
      titleSlot: true,
      columnSlot: true
    },
    {
      name: 'Local Network',
      tooltip: `
      <p>All servers except the Chipcore line can connect to&nbsp;the local network. It&nbsp;is&nbsp;physically isolated from the Internet and securely interconnects servers. It&nbsp;can be&nbsp;used to&nbsp;connect servers in&nbsp;one data center or&nbsp;across different ones.</p>
    `,
      dataIndex: 'localNetwork',
      titleSlot: true,
      columnSlot: true
    },
    {
      name: 'Configurability',
      dataIndex: 'changeAbility',
      columnSlot: true
    },
    {
      name: 'Monthly traffic',
      tooltip: `
      <p>The server rental price includes a&nbsp;free Internet traffic package. You can choose an&nbsp;unlimited traffic and 100 Mbps bandwidth option, or&nbsp;a&nbsp;30&nbsp;TB traffic and bandwidth that is&nbsp;limited only by&nbsp;the port bandwidth. Usually, it&nbsp;is&nbsp;1&nbsp;Gbps, but can be&nbsp;higher depending on&nbsp;the configuration. If&nbsp;you deplete your subscription package, you can always purchase more traffic/bandwidth. If&nbsp;you find the selected consumption profile unsuitable, you can easily change it&nbsp;in&nbsp;the control panel.</p>
    `,
      dataIndex: 'trafficVolume',
      titleSlot: true,
      columnSlot: true
    },
    {
      name: 'SLA',
      tooltip: `
      <p>
          SLA (Service Level Agreement) is&nbsp;an&nbsp;agreement specifying what services will be&nbsp;provided and how. This document outlines the guaranteed monthly service availability. If&nbsp;these conditions are not met, Selectel will make a&nbsp;refund in&nbsp;accordance with the        <a
          href="https://old.selectel.ru/media/files/docs_ru/conditions-dedicated.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          terms of use</a>.
      </p>
    `,
      dataIndex: 'sla',
      titleSlot: true
    },
    {
      dataIndex: 'price',
      columnSlot: true
    }
  ],
  ru: [
    {
      dataIndex: 'header',
      columnSlot: true
    },
    {
      name: 'Готовность к работе',
      dataIndex: 'readyToWork'
    },
    {
      name: '1 общий публичный IPv4-адрес',
      dataIndex: 'publicIpV4',
      columnSlot: true
    },
    {
      name: 'Автоустановка ОС и режим Rescue',
      tooltip: `
      <p>Для выделенных серверов в Selectel доступна автоматическая
        <a
          href="https://kb.selectel.ru/docs/servers-and-infrastructure/dedicated-servers/getting-started/automated_os_installation/"
          target="_blank"
          rel="noopener noreferrer"
        >
          установка операционной системы.
        </a>
      </p>
      <p>Если в работе ОС произошел сбой, а доступа к консоли нет, пригодится режим восстановления (Rescue). Он поможет запустить ОС, которая не зависит от основной системы и работает только в оперативной памяти.</p>
      <p>В состав этой ОС включены диагностические утилиты, с помощью которых можно восстановить доступ к серверу и хранимым на нем данным.</p>
    `,
      dataIndex: 'autoInstall',
      titleSlot: true,
      columnSlot: true
    },
    {
      name: 'KVM-консоль',
      tooltip: `
      <p>
        <a
          href="https://kb.selectel.ru/docs/servers-and-infrastructure/dedicated-servers/getting-started/kvm_console/"
          target="_blank"
          rel="noopener noreferrer"
        >
          KVM
        </a>
        (Keyboard, Video, Mouse) ― технология, имитирующая локальное подключение к серверу и пробрасывающая видеопоток с сервера в браузер. Она позволяет удаленно управлять оборудованием и использовать основные возможности IPMI.
      </p>
    `,
      dataIndex: 'kvmConsole',
      titleSlot: true,
      columnSlot: true
    },
    {
      name: 'Локальная сеть',
      tooltip: `
      <p>Все серверы, кроме линейки Chipcore, могут подключиться к локальной сети. Это физически изолированная от доступа в интернет сеть, которая безопасно связывает серверы между собой. С ее помощью можно соединить серверы в одном дата-центре или в разных.</p>
    `,
      dataIndex: 'localNetwork',
      titleSlot: true,
      columnSlot: true
    },
    {
      name: 'Возможность изменять конфигурацию',
      dataIndex: 'changeAbility',
      columnSlot: true
    },
    {
      name: 'Объем трафика в месяц',
      tooltip: `
      <p>В стоимость аренды сервера входит бесплатный пакет интернет-трафика. Вы можете выбрать вариант с безлимитным трафиком и шириной полосы 100 Мбит/с. Или с объемом трафика 30 ТБ и пропускной способностью, которая ограничивается только скоростью порта.Обычно это 1 Гбит/с, но может быть и выше, все зависит от конфигурации. Если включенного объема не хватило, всегда можно докупить пакеты трафика/полосы. А если не подошла модель потребления ― ее можно легко изменить в панели управления.</p>
    `,
      dataIndex: 'trafficVolume',
      titleSlot: true,
      columnSlot: true
    },
    {
      name: 'SLA',
      tooltip: `
      <p>
        SLA (Service Level Agreement) ― соглашение об уровне обслуживания, где указано, какие услуги будут предоставляться и как. В этом документе  описана гарантированная доступность услуги в месяц. При невыполнении указанных условий Selectel выплачивает компенсацию в соответствии с
        <a
          href="https://old.selectel.ru/media/files/docs_ru/conditions-dedicated.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          условиями использования</a>.
      </p>
    `,
      dataIndex: 'sla',
      titleSlot: true
    },
    {
      dataIndex: 'price',
      columnSlot: true
    }
  ]
}

const readyDesktop = {
  en: {
    header: {
      globalTitle: 'Off-the-shelf configurations',
      title: 'with consumer-grade CPUs',
      subtitle: 'Assembled, tested, and ready to work out of the box',
      tooltip: `
      <p>
        These are the Chipcore Line servers. They are based on&nbsp;popular desktop CPUs&nbsp;&mdash; Intel&reg; Celeron, Intel&reg; Core, and AMD&nbsp;Ryzen&trade;. Unlike home computers, Chipcore servers are hosted in&nbsp;Selectel data centers under continuous control to&nbsp;ensure 99.8% service availability.
        Such equipment is&nbsp;perfect for situations where the project load is&nbsp;spread across a&nbsp;network of&nbsp;servers. In&nbsp;this case, a&nbsp;short-term downtime of&nbsp;one server will not affect the overall project. In&nbsp;contrast to&nbsp;servers based on&nbsp;enterprise-grade CPUs, Chipcore Line features CPUs up&nbsp;to&nbsp;5GHz for seamless running of&nbsp;single-threaded applications. These servers are also noticeably cheaper.
      </p>
    `
    },
    readyToWork: 'from 2 to 60 minutes',
    trafficVolume: ['1 Gbps: 5 TB', '100 Mbps: Unlimited'],
    sla: '99,8%',
    price: {
      target: 'chipcore',
      subtitle: 'Daily or monthly',
      buttonText: 'Select server',
      dataGtmid: 'gtm-selectel-server-fastlink-desktop',
      scrollTo: 'preconfigured',
      eventData: 'isServersWithDesktopProcessors'
    },
    yesIcons: ['publicIpV4', 'autoInstall'],
    noIcons: ['kvmConsole', 'localNetwork', 'changeAbility']
  },
  ru: {
    header: {
      globalTitle: 'Готовые конфигурации',
      title: 'с десктопными процессорами',
      subtitle: 'Уже собраны, протестированы и готовы к работе сразу после заказа',
      tooltip: `
      <p>
        Это серверы Chipcore Line. Они построены на базе популярных десктопных процессоров ― Intel®️ Celeron, Intel®️ Core и AMD Ryzen™. В отличие от домашних компьютеров, Chipcore находятся в дата-центрах Selectel под постоянным контролем и обеспечивают 99,8% доступность сервисов.
        Такое оборудование отлично подойдет для ситуаций, когда нагрузка проекта распределена на сеть серверов. В этом случае кратковременный выход из строя одного сервера никак не отразится на работе проекта. В отличие от серверов с серверными процессорами, в Chipcore Line можно найти процессоры с частотой до 5 ГГц для удобной работы с однопоточными приложениями. А еще эти серверы ощутимо дешевле.
      </p>
    `
    },
    readyToWork: 'от 2 до 60 минут',
    trafficVolume: ['1 Гбит/с: 5 ТБ', '100 Мбит/с: Безлимит'],
    sla: '99,8%',
    price: {
      target: 'chipcore',
      subtitle: 'Посуточно или помесячно',
      buttonText: 'Выбрать сервер',
      dataGtmid: 'gtm-selectel-server-fastlink-desktop',
      scrollTo: 'preconfigured',
      eventData: 'isServersWithDesktopProcessors'
    },
    yesIcons: ['publicIpV4', 'autoInstall'],
    noIcons: ['kvmConsole', 'localNetwork', 'changeAbility']
  }
}

const readyServer = {
  en: {
    header: {
      globalTitle: 'Off-the-shelf configurations',
      dataHidden: true,
      title: 'with enterprise-grade CPUs',
      subtitle: 'Assembled, tested, and ready to work out of the box',
      tooltip: `
      <p>
        These servers are based on&nbsp;Intel&reg; Xeon and AMD&nbsp;EPYC&trade; CPUs. More than 40&nbsp;configurations are available. All components in&nbsp;these servers are streamlined for high load and uninterrupted operation.
        Many configurations feature more cores and RAM than consumer-grade servers. For example, our most powerful off-the-shelf server boasts 4&nbsp;sockets, 72&nbsp;cores, and 768&nbsp;GB RAM.
        Drives installed in&nbsp;servers with enterprise-grade CPUs are designed for higher IOPS, capable of&nbsp;processing more data and doing it&nbsp;faster. If&nbsp;something happens to&nbsp;a&nbsp;hard drive, it&nbsp;can be&nbsp;replaced without stopping the server. Some configurations have redundant power supplies, which can be&nbsp;replaced on&nbsp;the fly in&nbsp;case of&nbsp;failure
      </p>
    `
    },
    readyToWork: 'from 2 to 60 minutes',
    trafficVolume: ['1 Gbps: 30 TB', '100 Mbps: Unlimited'],
    sla: '100%',
    price: {
      target: 'server',
      subtitle: 'Daily or monthly',
      buttonText: 'Select server',
      dataGtmid: 'gtm-selectel-server-fastlink-server',
      scrollTo: 'preconfigured',
      eventData: 'isServersWithServerProcessors'
    },
    yesIcons: ['publicIpV4', 'kvmConsole', 'localNetwork', 'autoInstall'],
    noIcons: ['changeAbility']
  },
  ru: {
    header: {
      globalTitle: 'Готовые конфигурации',
      dataHidden: true,
      title: 'с серверными процессорами',
      subtitle: 'Уже собраны, протестированы и готовы к работе сразу после заказа',
      tooltip: `
      <p>
        Эти серверы построены на базе процессоров enterprise-класса ― Intel®️ Xeon и AMD EPYC™. Всего ― больше 40 конфигураций. В таких серверах все комплектующие оптимизированы под высокую нагрузку и бесперебойную работу.
        У многих конфигураций больше ядер и памяти, чем у серверов с десктопными процессорами. Например, у нашего самого мощного готового сервера ― 4 сокета, 72 ядра и 768 ГБ памяти.
        Диски в серверах с серверными процессорами рассчитаны на более высокие IOPS ― они обрабатывают больше данных и делают это быстрее. Если с диском что-то произошло, его можно заменить без остановки работы сервера. В некоторых конфигурациях резервируются блоки питания, в случае сбоя их тоже могут менять «на ходу».
      </p>
    `
    },
    readyToWork: 'от 2 до 60 минут',
    trafficVolume: ['1 Гбит/с: 30 ТБ', '100 Мбит/с: Безлимит'],
    sla: '100%',
    price: {
      target: 'server',
      subtitle: 'Посуточно или помесячно',
      buttonText: 'Выбрать сервер',
      dataGtmid: 'gtm-selectel-server-fastlink-server',
      scrollTo: 'preconfigured',
      eventData: 'isServersWithServerProcessors'
    },
    yesIcons: ['publicIpV4', 'kvmConsole', 'localNetwork', 'autoInstall'],
    noIcons: ['changeAbility']
  }
}

const randomConfig = {
  en: {
    header: {
      globalTitle: 'Custom configurations',
      subtitle: 'Build to your exact requirements within 5 days'
    },
    readyToWork: '3-5 days',
    trafficVolume: ['1 Gbps: 30 TB', '100 Mbps: Unlimited'],
    sla: '100%',
    price: {
      target: 'config',
      subtitle: 'Monthly only',
      buttonText: 'Build server',
      dataGtmid: 'gtm-selectel-server-fastlink-custom',
      scrollTo: 'preconfigured',
      eventData: 'isConfigurator'
    },
    yesIcons: ['publicIpV4', 'autoInstall', 'kvmConsole', 'localNetwork', 'changeAbility']
  },
  ru: {
    header: {
      globalTitle: 'Произвольные конфигурации',
      subtitle: 'Соберем под ваши требования в течение 5 дней'
    },
    readyToWork: '3-5 дней',
    trafficVolume: ['1 Гбит/с: 30 ТБ', '100 Мбит/с: Безлимит'],
    sla: '100%',
    price: {
      target: 'config',
      subtitle: 'Только помесячно',
      buttonText: 'Собрать сервер',
      dataGtmid: 'gtm-selectel-server-fastlink-custom',
      scrollTo: 'preconfigured',
      eventData: 'isConfigurator'
    },
    yesIcons: ['publicIpV4', 'autoInstall', 'kvmConsole', 'localNetwork', 'changeAbility']
  }
}

const restClient = new RestClient()

export default {
  name: 'DedicatedServersTable',
  components: {
    TableMain,
    TableTitleColumn,
    TableColumn,
    SvgNoIcon,
    SvgYesIcon
  },
  mixins: [addAdditionalData],
  async fetch() {
    if (this.getAllServersPrices.length) {
      this.servers = this.addAdditionalData(this.getAllServersPrices)
    } else {
      await this.fetchAllServersPrices()

      this.servers = this.addAdditionalData(this.getAllServersPrices)
    }
  },
  fetchOnServer: true,
  data() {
    return {
      titles: titles[this.$i18n.locale],
      readyDesktop: readyDesktop[this.$i18n.locale],
      readyServer: readyServer[this.$i18n.locale],
      randomConfig: randomConfig[this.$i18n.locale],
      servers: []
    }
  },
  computed: {
    titlesSlots() {
      return this.titles.filter(title => title.titleSlot)
    },
    filteredServers() {
      return this.servers
        .filter(server => server.isAvailable)
        .sort((a, b) => a.priceCollection.rub.month - b.priceCollection.rub.month)
    },
    chipcoreServers() {
      return this.filteredServers.filter(server => server.tariffLine === TariffLine.CHIPCORE)
    },
    configuratedServers() {
      return this.filteredServers.filter(server => server.tariffLine !== TariffLine.CHIPCORE)
    },
    chipcoreMinPrice() {
      const [chipcoreMinPriceServer] = this.chipcoreServers

      return chipcoreMinPriceServer
        ? chipcoreMinPriceServer.priceCollection.rub.month
        : 0
    },
    configuratedMinPrice() {
      const [configuratedMinPriceServer] = this.configuratedServers

      return configuratedMinPriceServer
        ? configuratedMinPriceServer.priceCollection.rub.month
        : 0
    },
    prices() {
      return {
        chipcore: this.chipcoreMinPrice,
        server: this.configuratedMinPrice,
        config: 3780
      }
    },
    ...mapGetters('rest-client/servers/all', ['getAllServersPrices'])
  },
  beforeDestroy() {
    this.$events.$off('dedicatedServersEvent')
  },
  methods: {
    setActiveTab(eventData) {
      this.$events.$emit('dedicatedServersEvent', eventData)
    },
    ...mapActions('rest-client/servers/all', ['fetchAllServersPrices'])
  }
}
</script>

<style lang="scss" scoped>
.tooltip-icon {
  margin-left: 3px;
  position: relative;
  top: -2px;
  cursor: pointer;
}

.dedicated-servers-table /deep/ .table-column__cell {
  position: relative;
}

.dedicated-servers-table /deep/ .table-column .table-column__cell:first-of-type {
  height: 100%;
}

.column-header--random {
  margin: -16px;
  padding: 16px;
  height: calc(100% + 32px);
  width: calc(100% + 32px);
  border-left: 1px solid transparentize($primary, 0.9);

  @media (max-width: $breakpoint-lg) {
    margin: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    position: static;
    border-left: none;
  }
}

.column-header__description {
  color: $primary65;
}

.column-header--ready {
  .column-header__global-title,
  .column-header__description {
    display: block;
    width: 200%;

    @media (max-width: $breakpoint-lg) {
      width: 100%;
    }
  }
}

.column-header--hidden {
  .column-header__global-title,
  .column-header__description {
    position: relative;
    left: -100%;
    opacity: 0;

    @media (max-width: $breakpoint-lg) {
      position: static;
      opacity: 1;
    }
  }
}

.column-header__title {
  padding-top: 24px;

  @media (max-width: $breakpoint-lg) {
    padding-top: 16px;
  }
}

.dedicated-servers-table__price {
  margin-bottom: 8px;
}

.dedicated-servers-table__price-description {
  color: $primary65;
  margin-bottom: 24px;

  @media (max-width: $breakpoint-md) {
    margin-bottom: 16px;
  }
}
</style>